import { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { Pastille, type TonPastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { useChangerStatutCours, type GestePossible, type GesteStatutCours, type StatutCours } from '../../lib/useCours';

/**
 * Statut d'un cours en ligne (« Mes cours », TF-443) : la pastille, et les
 * gestes que l'API propose — publier, repasser en brouillon, archiver,
 * désarchiver —, chacun avec ce qui le bloque. L'écran ne juge rien.
 */

const TON_STATUT: Readonly<Record<StatutCours, TonPastille>> = {
  BROUILLON: 'neutral',
  PUBLIE: 'success',
  ARCHIVE: 'info',
};

const CONFIRMATION: Readonly<Record<GesteStatutCours, string>> = {
  PUBLIER: 'Cours publié',
  REPASSER_EN_BROUILLON: 'Cours repassé en brouillon',
  ARCHIVER: 'Cours archivé',
  DESARCHIVER: 'Cours désarchivé : il repasse en brouillon',
};

export function PastilleStatutCours({
  statut,
  libelle,
  majuscules = false,
}: {
  statut: StatutCours;
  libelle: string;
  majuscules?: boolean;
}): JSX.Element {
  return <Pastille texte={majuscules ? libelle.toUpperCase() : libelle} ton={TON_STATUT[statut]} />;
}

function messageErreur(erreur: unknown): string {
  return erreur instanceof ErreurApi ? erreur.message : 'Changement de statut impossible. Réessayez.';
}

function useGesteStatut(coursId: string) {
  const toast = useToast();
  const changement = useChangerStatutCours();
  const executer = async (geste: GesteStatutCours): Promise<void> => {
    try {
      await changement.mutateAsync({ coursId, geste });
      toast(CONFIRMATION[geste]);
    } catch (erreur) {
      toast(messageErreur(erreur));
    }
  };
  return { executer, enCours: changement.isPending };
}

/** Menu ⋮ d'une carte de « Mes cours ». */
export function MenuStatutCours({
  coursId,
  titre,
  gestes,
}: {
  coursId: string;
  titre: string;
  gestes: readonly GestePossible[];
}): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const conteneur = useRef<HTMLDivElement>(null);
  const { executer, enCours } = useGesteStatut(coursId);

  useEffect(() => {
    if (!ouvert) return;
    const fermerHors = (evenement: PointerEvent) => {
      if (!conteneur.current?.contains(evenement.target as Node)) setOuvert(false);
    };
    const fermerEchap = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('pointerdown', fermerHors);
    document.addEventListener('keydown', fermerEchap);
    return () => {
      document.removeEventListener('pointerdown', fermerHors);
      document.removeEventListener('keydown', fermerEchap);
    };
  }, [ouvert]);

  return (
    <div ref={conteneur} className="relative">
      <button
        type="button"
        onClick={() => setOuvert((courant) => !courant)}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-label={`Statut du cours « ${titre} »`}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-codialis-navy hover:bg-white [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
      >
        <MoreVertical className="h-4 w-4" aria-hidden />
      </button>
      {ouvert && (
        <div
          role="menu"
          aria-label={`Statut du cours « ${titre} »`}
          className="absolute right-0 top-full z-20 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-100 bg-white p-1 text-left shadow-2xl"
        >
          {gestes.map((geste) => (
            <button
              key={geste.geste}
              type="button"
              role="menuitem"
              disabled={enCours || geste.refus !== null}
              onClick={() => {
                setOuvert(false);
                void executer(geste.geste);
              }}
              className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:hover:bg-transparent [@media(pointer:coarse)]:min-h-[44px]"
            >
              <span className={geste.refus ? 'text-codialis-gray' : ''}>{geste.libelle}</span>
              {geste.refus && <span className="mt-0.5 block text-xs font-normal text-codialis-gray">{geste.refus}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Boutons de statut de l'en-tête de « Créer un cours ». */
export function BoutonsStatutCours({
  coursId,
  gestes,
}: {
  coursId: string;
  gestes: readonly GestePossible[];
}): JSX.Element {
  const { executer, enCours } = useGesteStatut(coursId);
  const refus = gestes.filter((geste) => geste.refus !== null);
  return (
    <div className="flex min-w-0 flex-col items-start gap-1.5 lg:items-end">
      <div className="flex flex-wrap gap-2">
        {gestes.map((geste) => (
          <button
            key={geste.geste}
            type="button"
            disabled={enCours || geste.refus !== null}
            onClick={() => void executer(geste.geste)}
            className={
              geste.geste === 'PUBLIER'
                ? 'min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:cursor-not-allowed disabled:opacity-60'
                : 'min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-codialis-navy hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'
            }
          >
            {geste.geste === 'PUBLIER' ? 'Publier le cours' : geste.libelle}
          </button>
        ))}
      </div>
      {refus.map((geste) => (
        <p key={geste.geste} className="max-w-md text-xs text-codialis-gray lg:text-right">
          {geste.libelle} : {geste.refus}
        </p>
      ))}
    </div>
  );
}
